import { JobPosition } from '@features/hireflixJob/types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Close,
  Info,
  Delete,
  Videocam,
  PlaylistAddOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { hireflixCta, hireflixCtaHover } from '@styles/colors';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, Resolver } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { hireflixTemplateQuestions as templateQuestions } from '../constants';
import { useAddHireflixVideoInterview } from '../hooks/useAddHireflixVideoInterview';
import { useGetHireflixTemplateJobPositions } from '../hooks/useGetHireflixTemplateJobPositions';

const schema = yup.object().shape({
  job_ids: yup
    .array()
    .of(yup.number())
    .min(1, 'Select at least one job position'),
  questions: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required('Question is required'),
        description: yup.string().required('Answer is required'),
      }),
    )
    .min(1, 'At least one question is required'),
});

type FormValues = {
  job_ids: number[];
  questions: {
    title: string;
    description: string;
  }[];
};

const HireflixAddVideoInterviewModal = ({
  open,
  onClose,
  initialQuestions = [],
  showTemplateButtons = false,
  templateId,
  currentJobs,
}: {
  open: boolean;
  onClose: () => void;
  initialQuestions?: { title: string; description: string }[];
  showTemplateButtons?: boolean;
  templateId: number;
  currentJobs: any;
}) => {
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number | null>(
    null,
  );
  const [errorText, setErrorText] = useState<string>('');
  const [selectedItems, setSelectedItems] = useState<JobPosition[]>([]);
  const [localJobPositions, setLocalJobPositions] = useState<JobPosition[]>([]);

  const { jobPositionsData, isLoadingJobPositions } =
    useGetHireflixTemplateJobPositions({
      templateId,
      setErrorText,
    });

  const { addHireflixVideoInterview, isAddingVideoInterviewLoading } =
    useAddHireflixVideoInterview({
      setErrorText,
    });

  useEffect(() => {
    if (jobPositionsData?.job_listing) {
      const filtered = jobPositionsData.job_listing.filter(
        (jb: any) => !currentJobs?.some((c: any) => c.id === jb.id),
      );

      setLocalJobPositions(filtered);
    }
  }, [jobPositionsData, currentJobs]);

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: {
      job_ids: [],
      questions:
        initialQuestions && initialQuestions.length > 0
          ? initialQuestions
          : [{ title: '', description: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  const openTemplateDialog = (index: number) => {
    setActiveQuestionIndex(index);
    setTemplateDialogOpen(true);
  };

  const closeTemplateDialog = () => {
    setActiveQuestionIndex(null);
    setTemplateDialogOpen(false);
  };

  const handleSelectTemplate = (template: {
    title: string;
    description: string;
  }) => {
    if (activeQuestionIndex === null) return;
    setValue(`questions.${activeQuestionIndex}.title`, template.title);
    setValue(
      `questions.${activeQuestionIndex}.description`,
      template.description,
    );
    closeTemplateDialog();
  };

  const handleSelectAll = () => {
    if (selectedItems.length === localJobPositions.length) {
      setSelectedItems([]);
      setValue('job_ids', []);
    } else {
      setSelectedItems(localJobPositions);
      setValue(
        'job_ids',
        localJobPositions.map((job) => job.id),
      );
    }
  };

  const onSubmit = (values: FormValues) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success(
            data.message || 'HireFlix Interview Created successfully!',
          );
          setErrorText('');
          onClose();
        } else {
          setErrorText(data.message);
        }
      },
    };

    const payload = {
      template_id: templateId,
      job_ids: values.job_ids,
      questions: values.questions,
    };

    addHireflixVideoInterview(
      {
        ...payload,
      },
      options,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={onClose}
        >
          <Close />
        </IconButton>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto',
            borderRadius: '100%',
            backgroundColor: 'rgb(243, 232, 255)',
            mb: 1,
          }}
        >
          <Videocam sx={{ fontSize: 40, color: hireflixCta }} />
        </Box>
        <Typography variant="h3" fontWeight="600" sx={{ p: 3 }}>
          Add New Hireflix Interview
        </Typography>
      </DialogTitle>

      <DialogContent>
        {errorText && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorText}
          </Alert>
        )}

        <Stack spacing={3} py={2}>
          <FormControl sx={{ width: '100%', minWidth: 120 }}>
            <Typography display="flex" alignItems="center" mb={1.5} gap={1}>
              Select Job Positions
              <Tooltip
                title="You can select multiple job positions, but at least 1 question is required."
                placement="top"
                arrow
              >
                <Info sx={{ fontSize: '16px' }} color="primary" />
              </Tooltip>
            </Typography>

            <Box
              sx={{
                position: 'relative',
                marginBottom: 2,
                cursor: isLoadingJobPositions ? 'not-allowed' : '',
              }}
            >
              {isLoadingJobPositions && (
                <Box
                  display="flex"
                  justifyContent="center"
                  sx={{
                    position: 'absolute',
                    top: 9,
                    right: 35,
                    pointerEvents: 'none',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              <Controller
                name="job_ids"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    multiple
                    size="small"
                    displayEmpty
                    disabled={isLoadingJobPositions}
                    value={field.value}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const valArray = Array.isArray(raw)
                        ? (raw as (string | number)[])
                        : [raw as string | number];

                      if (valArray.includes('select-all')) {
                        handleSelectAll();
                      } else {
                        const numeric = valArray
                          .filter((v) => v !== 'select-all')
                          .map((v) =>
                            typeof v === 'string' ? Number(v) : v,
                          ) as number[];

                        const selected = localJobPositions.filter((job) =>
                          numeric.includes(job.id),
                        );
                        setSelectedItems(selected);
                        field.onChange(numeric);
                      }
                    }}
                    sx={{
                      width: '100%',
                      opacity: isLoadingJobPositions ? '0.5' : '1',
                      '& .MuiSelect-select': {
                        cursor: isLoadingJobPositions ? 'not-allowed' : '',
                      },
                    }}
                    renderValue={(selected) =>
                      (selected as number[]).length === 0 ? (
                        <Typography color="textSecondary" sx={{ pl: 1 }}>
                          <em>Select a Position or Multiple</em>
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {localJobPositions
                            .filter((job) =>
                              (field.value as number[]).includes(job.id),
                            )
                            .map((job) => (
                              <Chip key={job.id} label={job.s_title} />
                            ))}
                        </Box>
                      )
                    }
                  >
                    <MenuItem value="select-all" onClick={handleSelectAll}>
                      {selectedItems?.length === localJobPositions.length
                        ? 'Deselect All'
                        : 'Select All'}
                    </MenuItem>

                    {[...localJobPositions]
                      .sort((a, b) => a.s_title.localeCompare(b.s_title))
                      .map((job: any) => (
                        <MenuItem key={job.id} value={job.id}>
                          {job.s_title}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              {errors.job_ids && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {errors.job_ids.message}
                </Typography>
              )}
            </Box>
          </FormControl>

          {/* Questions Section */}
          <Stack spacing={2}>
            {fields.map((qField, index) => (
              <Box
                key={qField.id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 8,
                  p: 3,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    backgroundColor: '#f5f7fa',
                    borderRadius: 6,
                    p: 1.2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    Question # {index + 1}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showTemplateButtons && (
                      <Button
                        size="small"
                        onClick={() => openTemplateDialog(index)}
                        sx={{
                          color: hireflixCta,
                          fontSize: '14px',
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'rgb(243, 232, 255)',
                            borderColor: hireflixCta,
                          },
                        }}
                        startIcon={
                          <PlaylistAddOutlined sx={{ fontSize: 16 }} />
                        }
                      >
                        Add From Templates
                      </Button>
                    )}

                    {fields.length > 1 && (
                      <Tooltip title="Delete Question" placement="top" arrow>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => remove(index)}
                        >
                          <Delete sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>

                <Stack spacing={2}>
                  <Controller
                    name={`questions.${index}.title`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Question Title"
                        fullWidth
                        size="small"
                        error={!!errors.questions?.[index]?.title}
                        helperText={errors.questions?.[index]?.title?.message}
                      />
                    )}
                  />

                  <Controller
                    name={`questions.${index}.description`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Full Question"
                        fullWidth
                        size="small"
                        multiline
                        rows={4}
                        error={!!errors.questions?.[index]?.description}
                        helperText={
                          errors.questions?.[index]?.description?.message
                        }
                      />
                    )}
                  />
                </Stack>
              </Box>
            ))}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWarp: 'wrap',
                pt: 2,
                pb: 8,
              }}
            >
              <Button
                variant="outlined"
                size="small"
                sx={{
                  fontSize: '14px',
                  px: 2,
                  textTransform: 'none',
                  borderColor: hireflixCta,
                  color: hireflixCta,
                  '&:hover': {
                    backgroundColor: 'rgb(243, 232, 255)',
                    borderColor: hireflixCta,
                  },
                }}
                disabled={
                  isAddingVideoInterviewLoading ||
                  isLoadingJobPositions ||
                  fields.length >= 5
                }
                onClick={() => append({ title: '', description: '' })}
              >
                + Add Question
              </Button>

              {fields.length >= 5 && (
                <Typography color="text.secondary" variant="body2">
                  Video interviews allow a maximum of 5 questions
                </Typography>
              )}
            </Box>
          </Stack>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              color="info"
              onClick={onClose}
              disabled={isAddingVideoInterviewLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit(onSubmit)}
              disabled={isAddingVideoInterviewLoading || isLoadingJobPositions}
              startIcon={
                isAddingVideoInterviewLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                backgroundColor: hireflixCta,
                '&:hover': {
                  backgroundColor: hireflixCtaHover,
                },
              }}
            >
              Add Hireflix Interview
            </Button>
          </Box>
        </Stack>
      </DialogContent>

      {/* Template Picker Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={closeTemplateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: '600' }}>
          Select a Templated Interview Question
        </DialogTitle>
        <DialogContent>
          <List>
            {templateQuestions.map((q) => (
              <div key={q.id}>
                <ListItemButton
                  onClick={() =>
                    handleSelectTemplate({
                      title: q.title,
                      description: q.description,
                    })
                  }
                >
                  <ListItemText primary={q.title} secondary={q.description} />
                </ListItemButton>
                <Divider />
              </div>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default HireflixAddVideoInterviewModal;
